import { announce } from "@kovernow/flows/announce";
import {
  addItemQueryKeys,
  collectorSections,
  collectorUpdateErrorTarget,
  guardLegacyEditStep,
  insuredAmountField,
  isCollectorCategory,
  LEGACY_EDIT_STEPS,
  parseLegacyEditStep,
  prefillFromItem,
  prefillLegacyFromItem,
  todayIn,
  type AddItemFields,
  type AddItemStep,
  type CollectorCategory,
  type CollectorUpdatePayload,
  type CollectorValues,
  type LegacyEditStep,
} from "@kovernow/flows/add_item";
import { toRecoveryAction, type RecoveryAction } from "@kovernow/flows/recovery";
import { createUploadQueue } from "@kovernow/flows/uploads";
import {
  canEditOrDelete,
  isCollectorItem,
  isLegacyItem,
  vaultQueryKeys,
  withDocuments,
  type VaultItem,
} from "@kovernow/flows/vault";
import { ApiError } from "@kovernow/flows/recovery";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { Page } from "../../components/Page";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { CollectorForm } from "../add-item/CollectorForm";
import type { CollectorFormSection } from "../add-item/CollectorSectionStep";
import { ConditionStep } from "../add-item/ConditionStep";
import { DetailsStep } from "../add-item/DetailsStep";
import type { ServerFieldErrors } from "../add-item/shared";
import { EditPhotosStep } from "./EditPhotosStep";
import { isItemHasPolicy, isNotFound, ItemNotFound, storeUpdatedItem, useItem, useItemName, type DetailNavState } from "./shared";

const LEGACY_TITLE_KEYS: Record<LegacyEditStep, string> = {
  details: "addItem.details.title",
  condition: "addItem.condition.title",
  photos: "itemDetail.edit.photos",
};

/**
 * `/vault/:itemId/edit?step=`: the W-4/W-5 forms on a stored item. A
 * collector item gets its schema sections (no documents); a legacy item
 * gets details, condition and photos, prefilled. Drafts are never read or
 * written. `PUT /items` 2xx → the item from the response, Vault and Home
 * refreshed, "{name} updated", back to detail. An item with a policy
 * redirects to detail; a 409 `item_has_policy` returns there with a notice.
 */
export function EditItem() {
  const t = useT();
  const { itemId = "" } = useParams();
  const itemQ = useItem(itemId);
  const item = itemQ.data;

  if (itemQ.isPending) {
    return (
      <Page titleKey="nav.editItem" flowKey="nav.vault">
        <p aria-busy="true" className="text-muted-foreground">
          {t("common.loading")}
        </p>
      </Page>
    );
  }
  if (!item) {
    if (isNotFound(itemQ.error)) return <ItemNotFound />;
    return (
      <Page titleKey="nav.editItem" flowKey="nav.vault">
        <div data-testid="item-error">
          <Recovery action={toRecoveryAction(itemQ.error)} onRetry={() => void itemQ.refetch()} retryable />
        </div>
      </Page>
    );
  }
  const detail = `/vault/${encodeURIComponent(item._id)}`;
  if (!canEditOrDelete(item) || !(isLegacyItem(item) || isCollectorItem(item))) {
    return <Navigate to={detail} replace />;
  }
  return <EditForm key={item._id} item={item} />;
}

function EditForm({ item }: { item: VaultItem }) {
  const t = useT();
  const services = useServices();
  const { vaultApi, addItemApi, config, format } = services;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const nameOf = useItemName();
  const account = useQuery({ queryKey: addItemQueryKeys.account(), queryFn: () => addItemApi.account() });
  const ownerId = account.data?.id ?? null;
  const detail = `/vault/${encodeURIComponent(item._id)}`;
  const category = item.classification;
  const collector: CollectorCategory | null = isCollectorCategory(category) ? category : null;

  const [serverErrors, setServerErrors] = useState<ServerFieldErrors>({});
  const [recovery, setRecovery] = useState<RecoveryAction | null>(null);
  const [saving, setSaving] = useState(false);
  const clearServerError = useCallback(
    (f: string) =>
      setServerErrors((s) => {
        if (!s[f]) return s;
        const next = { ...s };
        delete next[f];
        return next;
      }),
    [],
  );

  // Legacy form state (lifted so ?step= changes keep it).
  const [fields, setFieldsState] = useState<AddItemFields>(() => prefillLegacyFromItem(item));
  const setFields = useCallback((update: (f: AddItemFields) => AddItemFields) => setFieldsState(update), []);
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const [queue] = useState(() => createUploadQueue({ adapter: services.uploads, convertHeic: services.convertHeic }));
  const inFlight = useRef(false);
  // Collector form values (lifted for the same reason).
  const [collectorValues, setCollectorValues] = useState<CollectorValues>(() =>
    collector ? prefillFromItem(collector, item) : {},
  );

  const now = new Date();
  const ctx = { now, today: todayIn(config.timeZone, now), currency: config.currency };

  // ------------------------------------------------------------ steps
  const sections = collector ? collectorSections(collector).filter((s): s is CollectorFormSection => s !== "documents") : [];
  const rawStep = searchParams.get("step");
  const legacyStep = collector ? null : guardLegacyEditStep(parseLegacyEditStep(rawStep), fields, ctx);
  const collectorSection: CollectorFormSection | null = collector
    ? ((sections as string[]).includes(rawStep ?? "") ? (rawStep as CollectorFormSection) : sections[0]!)
    : null;
  const step = (legacyStep ?? collectorSection)!;

  const goTo = useCallback(
    (s: string, opts?: { replace?: boolean }) => navigate({ search: `?step=${encodeURIComponent(s)}` }, { replace: opts?.replace }),
    [navigate],
  );
  useEffect(() => {
    if (legacyStep && rawStep !== legacyStep) goTo(legacyStep, { replace: true });
  }, [legacyStep, rawStep, goTo]);

  const steps: readonly string[] = collector ? sections : LEGACY_EDIT_STEPS;
  const back = useCallback(() => {
    const i = steps.indexOf(step);
    if (i <= 0) {
      navigate(detail);
      return;
    }
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else goTo(steps[i - 1]!, { replace: true });
  }, [steps, step, navigate, detail, goTo]);

  // Focus the step's h2 on every step change (not on first load).
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const lastStep = useRef<string | null>(location.key === "default" ? step : null);
  useEffect(() => {
    if (lastStep.current === step) return;
    const first = lastStep.current === null && location.key === "default";
    lastStep.current = step;
    if (!first) headingRef.current?.focus();
  });

  // ------------------------------------------------------------ save
  const onSaved = (updated: VaultItem) => {
    storeUpdatedItem(queryClient, updated);
    queue.clear();
    announce(t("itemDetail.announce.updated", { name: nameOf(updated) }));
    navigate(detail);
  };
  const onApiError = (err: unknown): boolean => {
    if (!isItemHasPolicy(err)) return false;
    void queryClient.invalidateQueries({ queryKey: vaultQueryKeys.item(item._id), exact: true });
    navigate(detail, { state: { notice: "item_has_policy" } satisfies DetailNavState });
    return true;
  };
  const onFieldError = (errStep: AddItemStep | string, field: string, messageKey: string) => {
    setServerErrors((s) => ({ ...s, [field]: messageKey }));
    if (errStep !== "photos" && errStep !== step) goTo(errStep, { replace: true });
  };

  const lastPayload = useRef<CollectorUpdatePayload | null>(null);
  const saveCollector = async (payload: CollectorUpdatePayload) => {
    if (saving || !collector) return;
    lastPayload.current = payload;
    setSaving(true);
    setRecovery(null);
    try {
      const updated = await vaultApi.update({ ...withDocuments(item, item.documents ?? [], config.currency), ...payload });
      onSaved(updated);
    } catch (err) {
      const code = err instanceof ApiError ? err.problem?.errorCode : undefined;
      const target = code ? collectorUpdateErrorTarget(code) : undefined;
      if (target) {
        const field =
          target.field === "insuredAmount"
            ? collector === "wine" || collector === "whisky"
              ? "pricePerBottle"
              : insuredAmountField(collectorValues)
            : target.field;
        onFieldError(target.step, field, `errors.${code}`);
      } else if (!onApiError(err)) {
        setRecovery(toRecoveryAction(err));
      }
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------ render
  const name = nameOf(item);
  const stepTitle = collector ? t(`addItem.steps.${step}`) : t(LEGACY_TITLE_KEYS[step as LegacyEditStep]);
  const stepOf = t("addItem.stepOf", { n: format.number(steps.indexOf(step) + 1), total: format.number(steps.length) });
  const page = (body: ReactNode) => (
    <Page
      titleKey="addItem.docTitle"
      titleValues={{ stepOf, step: stepTitle }}
      flowKey="nav.editItem"
      headingKey="nav.editItem"
      focusKey={null}
    >
      <p className="mb-4 text-muted-foreground" data-testid="edit-item-name">
        {name}
      </p>
      {body}
    </Page>
  );

  if (collector) {
    return page(
      <div data-testid="edit-item" data-step={step} className="flex flex-col gap-4">
        <CollectorForm
          category={collector}
          editItem={item}
          section={collectorSection!}
          onSection={(s) => goTo(s)}
          values={collectorValues}
          setValues={setCollectorValues}
          serverErrors={serverErrors}
          clearServerError={clearServerError}
          onSave={(p) => void saveCollector(p)}
        />
        <p role="status" className="text-sm text-muted-foreground">
          {saving ? t("addItem.saving") : ""}
        </p>
        {recovery ? (
          <div data-testid="edit-error">
            <Recovery action={recovery} onRetry={() => lastPayload.current && void saveCollector(lastPayload.current)} />
          </div>
        ) : null}
      </div>,
    );
  }

  const stepProps = {
    fields,
    setFields,
    goTo: (s: AddItemStep) => goTo(s),
    back,
    headingRef,
    serverErrors,
    clearServerError,
  };
  return page(
    <div data-testid="edit-item" data-step={step} className="flex flex-col gap-4">
      <p className="text-sm font-semibold text-muted-foreground" data-testid="step-of">
        {stepOf}
      </p>
      {step === "details" ? (
        <DetailsStep {...stepProps} ctx={ctx} declaration={undefined} editMode onContinue={() => goTo("condition")} />
      ) : step === "condition" ? (
        <ConditionStep {...stepProps} onContinue={() => goTo("photos")} />
      ) : (
        <EditPhotosStep
          item={item}
          fields={fields}
          removed={removed}
          setRemoved={setRemoved}
          queue={queue}
          ownerId={ownerId}
          inFlight={inFlight}
          headingRef={headingRef}
          serverErrors={serverErrors}
          clearServerError={clearServerError}
          back={back}
          onSaved={onSaved}
          onFieldError={onFieldError}
          onApiError={onApiError}
        />
      )}
    </div>,
  );
}
